/**
 * NewsWidget component for TG Dashboard
 * Displays latest news with expandable content
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import ReactMarkdown from 'react-markdown'
import { Newspaper, ChevronDown, ChevronUp, Pin, Clock, Loader2 } from 'lucide-react'
import { formatDistanceToNow, format } from 'date-fns'
import { sv } from 'date-fns/locale'
import { getNewsForSubcontractor, markNewsAsRead } from '../services/news'
import type { NewsWithReadStatus } from '../types/news'

interface NewsWidgetProps {
  subcontractorId: string
  maxItems?: number
}

export function NewsWidget({ subcontractorId, maxItems = 5 }: NewsWidgetProps) {
  const [expandedNewsId, setExpandedNewsId] = useState<string | null>(null)

  const { data: news = [], isLoading, refetch } = useQuery({
    queryKey: ['news-for-tg', subcontractorId],
    queryFn: () => getNewsForSubcontractor(subcontractorId),
    enabled: !!subcontractorId
  })

  const displayedNews = news.slice(0, maxItems)
  const unreadCount = news.filter(n => !n.is_read).length

  const handleExpand = async (newsItem: NewsWithReadStatus) => {
    if (expandedNewsId === newsItem.id) {
      setExpandedNewsId(null)
    } else {
      setExpandedNewsId(newsItem.id)
      // Mark as read when expanded
      if (!newsItem.is_read) {
        await markNewsAsRead(newsItem.id, subcontractorId)
        refetch()
      }
    }
  }

  if (isLoading) {
    return (
      <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700 p-6">
        <div className="flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-nature-400" />
        </div>
      </div>
    )
  }

  if (displayedNews.length === 0) {
    return (
      <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
            <Newspaper className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
          <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
            Nyheter
          </h3>
        </div>
        <p className="text-nature-500 dark:text-nature-400 text-sm">
          Inga nyheter just nu.
        </p>
      </div>
    )
  }

  return (
    <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700">
      {/* Header */}
      <div className="px-6 py-4 border-b border-nature-200 dark:border-nature-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
              <Newspaper className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
              Nyheter
            </h3>
          </div>
          {unreadCount > 0 && (
            <span className="px-2 py-1 text-xs font-medium bg-blue-500 text-white rounded-full">
              {unreadCount} nya
            </span>
          )}
        </div>
      </div>

      {/* News List */}
      <div className="divide-y divide-nature-200 dark:divide-nature-700">
        {displayedNews.map((newsItem) => (
          <div key={newsItem.id} className="relative">
            <button
              onClick={() => handleExpand(newsItem)}
              className={`w-full px-6 py-4 text-left hover:bg-nature-50 dark:hover:bg-nature-700/50 transition-colors ${
                !newsItem.is_read ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    {newsItem.is_pinned && (
                      <Pin className="h-3 w-3 text-amber-500 flex-shrink-0" />
                    )}
                    {!newsItem.is_read && (
                      <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
                    )}
                    <h4 className={`font-medium truncate ${
                      !newsItem.is_read
                        ? 'text-nature-900 dark:text-nature-100'
                        : 'text-nature-700 dark:text-nature-300'
                    }`}>
                      {newsItem.title}
                    </h4>
                  </div>
                  {newsItem.summary && expandedNewsId !== newsItem.id && (
                    <p className="text-sm text-nature-500 dark:text-nature-400 mt-1 line-clamp-1">
                      {newsItem.summary}
                    </p>
                  )}
                  <div className="flex items-center space-x-2 mt-2 text-xs text-nature-400">
                    <Clock className="h-3 w-3" />
                    <span>
                      {formatDistanceToNow(new Date(newsItem.published_at), {
                        addSuffix: true,
                        locale: sv
                      })}
                    </span>
                    <span>•</span>
                    <span>{newsItem.author_name}</span>
                  </div>
                </div>
                <div className="ml-4 flex-shrink-0">
                  {expandedNewsId === newsItem.id ? (
                    <ChevronUp className="h-5 w-5 text-nature-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-nature-400" />
                  )}
                </div>
              </div>
            </button>

            {/* Expanded Content */}
            {expandedNewsId === newsItem.id && (
              <div className="px-6 pb-4 bg-nature-50/50 dark:bg-nature-900/30">
                <div className="pt-2 border-t border-nature-200 dark:border-nature-700">
                  <div className="prose prose-sm dark:prose-invert max-w-none">
                    <ReactMarkdown>{newsItem.content}</ReactMarkdown>
                  </div>
                  <p className="text-xs text-nature-400 mt-4">
                    Publicerad {format(new Date(newsItem.published_at), 'yyyy-MM-dd HH:mm', { locale: sv })}
                  </p>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Show more indicator */}
      {news.length > maxItems && (
        <div className="px-6 py-3 border-t border-nature-200 dark:border-nature-700 text-center">
          <span className="text-sm text-nature-500 dark:text-nature-400">
            +{news.length - maxItems} fler nyheter
          </span>
        </div>
      )}
    </div>
  )
}
